import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import ToastContext from "../contexts/ToastContext";
import { FaFileUpload, FaFileAlt, FaTrash, FaCheckCircle } from "react-icons/fa";
import "./InscreverEdital.css";

const MAX_FILE_SIZE_MB = 100;
const MAX_TOTAL_UPLOAD_MB = 500;

const isAllowedFileType = (file) => {
  const allowed = [
    "image/jpeg",
    "image/png",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.oasis.opendocument.text",
    "text/plain",
  ];
  return allowed.includes(file?.type);
};

const FileInput = ({ label, onChange, required, fileName }) => (
  <div className="form-group upload-field">
    <label>
      <FaFileAlt />
      <span>{label}</span>
      {required && <span className="required-marker">*</span>}
    </label>
    <div className="upload-controls">
      <label className="btn-modern btn-file">
        <FaFileUpload size={18} />
        <span>Escolher arquivo</span>
        <input
          type="file"
          onChange={onChange}
          accept=".png,.jpg,.jpeg,.pdf"
          required={required}
          className="visually-hidden"
        />
      </label>
      {fileName && <span className="upload-selected">{fileName}</span>}
    </div>
  </div>
);

const MultiFileInput = ({ label, onAddMany, files, onRemove }) => {
  const inputRef = useRef(null);

  const handleSelection = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length > 0) {
      onAddMany(selectedFiles);
    }
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  return (
    <div className="form-group upload-field">
      <label>
        <FaFileAlt />
        <span>{label}</span>
      </label>
      <div className="upload-controls">
        <label className="btn-modern btn-file">
          <FaFileUpload size={18} />
          <span>Escolher arquivo(s)</span>
          <input
            type="file"
            multiple
            ref={inputRef}
            onChange={handleSelection}
            accept=".png,.jpg,.jpeg,.pdf"
            className="visually-hidden"
          />
        </label>
        <span className="upload-selected upload-selected-auto">
          Ao selecionar, os arquivos já entram na lista.
        </span>
      </div>
      <p className="upload-help-text">Você pode selecionar vários arquivos de uma vez.</p>
      {files.length > 0 && (
        <ul className="attachment-list">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="attachment-item">
              <span className="attachment-name">{f.name}</span>
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="ghost-button button-circle"
                aria-label={`Remover ${f.name}`}
              >
                <FaTrash />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

function InscreverEdital() {
  const { edital_id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);
  const [cargos, setCargos] = useState([]);
  const [selectedCargo, setSelectedCargo] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [files, setFiles] = useState({
    COMPROVANTE_RG: null,
    COMPROVANTE_CPF: null,
    COMPROVANTE_ENDERECO: null,
    TEMPO_SERVICO: [],
    DIPLOMA_GRADUACAO: [],
    CERTIFICADO_CURSO: [],
  });

  const requiredKeys = [
    "COMPROVANTE_RG",
    "COMPROVANTE_CPF",
    "COMPROVANTE_ENDERECO",
  ];

  const requiredDone = requiredKeys.filter((key) => Boolean(files[key])).length;
  const allFilesSelected = [
    files.COMPROVANTE_RG,
    files.COMPROVANTE_CPF,
    files.COMPROVANTE_ENDERECO,
    ...files.TEMPO_SERVICO,
    ...files.DIPLOMA_GRADUACAO,
    ...files.CERTIFICADO_CURSO,
  ].filter(Boolean);
  const totalBytes = allFilesSelected.reduce((acc, file) => acc + (file.size || 0), 0);

  useEffect(() => {
    async function fetchCargos() {
      try {
        const { data } = await api.get(`/cargos/edital/${edital_id}`);
        setCargos(data);
      } catch (error) {
        addToast("Erro ao buscar cargos do edital.", "error");
      }
    }
    fetchCargos();
  }, [edital_id, addToast]);

  const logSubmitDiagnostic = (stage, details = {}) => {
    const payload = {
      stage,
      timestamp: new Date().toISOString(),
      edital_id,
      selectedCargo,
      requiredDocs: {
        COMPROVANTE_RG: Boolean(files.COMPROVANTE_RG),
        COMPROVANTE_CPF: Boolean(files.COMPROVANTE_CPF),
        COMPROVANTE_ENDERECO: Boolean(files.COMPROVANTE_ENDERECO),
      },
      totalFiles: allFilesSelected.length,
      totalBytes,
      online: typeof navigator !== "undefined" ? navigator.onLine : true,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
      ...details,
    };

    console.error("[InscricaoDiagnostic]", payload);
    try {
      sessionStorage.setItem("inscricao:lastDiagnostic", JSON.stringify(payload));
    } catch (_e) {
      // Ignore storage failures (private mode/quota) and keep flow running.
    }
  };

  const validateFilesForUpload = (incomingFiles) => {
    const invalidByType = incomingFiles.find((file) => !isAllowedFileType(file));
    if (invalidByType) {
      addToast(`Arquivo não permitido: ${invalidByType.name}.`, "error");
      return false;
    }

    const oversize = incomingFiles.find(
      (file) => Number(file.size || 0) > MAX_FILE_SIZE_MB * 1024 * 1024
    );
    if (oversize) {
      addToast(
        `O arquivo ${oversize.name} ultrapassa ${MAX_FILE_SIZE_MB} MB.`,
        "error"
      );
      return false;
    }

    const incomingBytes = incomingFiles.reduce((acc, file) => acc + Number(file.size || 0), 0);
    if (totalBytes + incomingBytes > MAX_TOTAL_UPLOAD_MB * 1024 * 1024) {
      addToast(
        `O envio total ultrapassa ${MAX_TOTAL_UPLOAD_MB} MB. Remova alguns arquivos.`,
        "error"
      );
      return false;
    }

    return true;
  };

  const validateSingleFile = (file) => {
    if (!file) return true;
    if (!isAllowedFileType(file)) {
      addToast(`Arquivo não permitido: ${file.name}.`, "error");
      return false;
    }
    if (Number(file.size || 0) > MAX_FILE_SIZE_MB * 1024 * 1024) {
      addToast(`O arquivo ${file.name} ultrapassa ${MAX_FILE_SIZE_MB} MB.`, "error");
      return false;
    }
    return true;
  };

  const handleFileChange = (type, file) => {
    if (!validateSingleFile(file)) {
      return;
    }
    setFiles((prev) => ({ ...prev, [type]: file }));
  };

  const handleMultiFileAddMany = (type, incomingFiles) => {
    if (!validateFilesForUpload(incomingFiles)) return;
    setFiles((prev) => ({ ...prev, [type]: [...prev[type], ...incomingFiles] }));
  };

  const handleFileRemove = (type, index) => {
    setFiles((prev) => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);

    if (!selectedCargo) {
      addToast("Por favor, selecione um cargo.", "error");
      logSubmitDiagnostic("blocked_validation", { reason: "missing_cargo" });
      setIsSubmitting(false);
      return;
    }
    if (
      !files.COMPROVANTE_RG ||
      !files.COMPROVANTE_CPF ||
      !files.COMPROVANTE_ENDERECO
    ) {
      addToast("Por favor, anexe todos os documentos obrigatórios.", "error");
      logSubmitDiagnostic("blocked_validation", { reason: "missing_required_docs" });
      setIsSubmitting(false);
      return;
    }

    if (
      !validateSingleFile(files.COMPROVANTE_RG) ||
      !validateSingleFile(files.COMPROVANTE_CPF) ||
      !validateSingleFile(files.COMPROVANTE_ENDERECO)
    ) {
      logSubmitDiagnostic("blocked_validation", { reason: "invalid_required_doc" });
      setIsSubmitting(false);
      return;
    }

    if (totalBytes > MAX_TOTAL_UPLOAD_MB * 1024 * 1024) {
      addToast(
        `O tamanho total dos arquivos ultrapassa ${MAX_TOTAL_UPLOAD_MB} MB.`,
        "error"
      );
      logSubmitDiagnostic("blocked_validation", { reason: "total_size_limit" });
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData();
    formData.append("cargo_id", selectedCargo);
    formData.append("COMPROVANTE_RG", files.COMPROVANTE_RG);
    formData.append("COMPROVANTE_CPF", files.COMPROVANTE_CPF);
    formData.append("COMPROVANTE_ENDERECO", files.COMPROVANTE_ENDERECO);
    files.TEMPO_SERVICO.forEach((file) =>
      formData.append("TEMPO_SERVICO", file)
    );
    files.DIPLOMA_GRADUACAO.forEach((file) =>
      formData.append("DIPLOMA_GRADUACAO", file)
    );
    files.CERTIFICADO_CURSO.forEach((file) =>
      formData.append("CERTIFICADO_CURSO", file)
    );

    try {
      logSubmitDiagnostic("submit_started");
      await api.post(
        `/inscricoes/edital/${edital_id}/subscribe-with-docs`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      logSubmitDiagnostic("submit_success");
      addToast("Inscrição realizada com sucesso!", "success");
      navigate("/minhas-inscricoes");
    } catch (error) {
      const status = error.response?.status;
      const serverCode = error.response?.data?.code;
      const serverMessage = error.response?.data?.message;

      logSubmitDiagnostic("submit_failed", {
        status,
        serverCode,
        serverMessage,
        errorMessage: error.message,
      });

      if (error.response?.data?.code === "INCOMPLETE_PROFILE") {
        addToast(error.response.data.message, "error");
        navigate("/meu-perfil");
      } else if (!error.response) {
        addToast(
          "Não foi possível concluir a inscrição por falha de conexão. Verifique a internet e tente novamente.",
          "error"
        );
      } else if (status === 413) {
        addToast("Os arquivos enviados são muito grandes para o servidor.", "error");
      } else {
        addToast(
          serverMessage || "Erro ao realizar inscrição.",
          "error"
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container inscricao-page">
      <div className="inscricao-hero">
        <h1>Inscrição no Edital</h1>
        <p>
          Preencha os dados de forma tranquila. Os documentos opcionais entram
          automaticamente ao selecionar.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="inscricao-form-wrap section-spacing">
        <div className="upload-summary-card inscricao-progress-badge">
          <p>
            <FaCheckCircle />
            <strong> Progresso:</strong> {requiredDone}/3 documentos obrigatórios anexados.
          </p>
        </div>

        <div className="inscricao-layout">
          <div className="inscricao-column">
            <section className="surface-section inscricao-section">
              <h2>Seleção de Cargo</h2>
              <div className="form-group">
                <label htmlFor="cargo">Cargo desejado</label>
                <select
                  id="cargo"
                  value={selectedCargo}
                  onChange={(e) => setSelectedCargo(e.target.value)}
                  required
                >
                  <option value="" disabled>
                    Selecione o cargo desejado
                  </option>
                  {cargos.map((cargo) => (
                    <option key={cargo.id} value={cargo.id}>
                      {cargo.nome_cargo}
                    </option>
                  ))}
                </select>
              </div>
            </section>

            <section className="surface-section inscricao-section inscricao-section-required">
              <h2>Documentos Obrigatórios</h2>
              <FileInput
                label="Comprovante de RG"
                onChange={(e) =>
                  handleFileChange("COMPROVANTE_RG", e.target.files[0])
                }
                required
                fileName={files.COMPROVANTE_RG ? files.COMPROVANTE_RG.name : ""}
              />
              <FileInput
                label="Comprovante de CPF"
                onChange={(e) =>
                  handleFileChange("COMPROVANTE_CPF", e.target.files[0])
                }
                required
                fileName={
                  files.COMPROVANTE_CPF ? files.COMPROVANTE_CPF.name : ""
                }
              />
              <FileInput
                label="Comprovante de Endereço"
                onChange={(e) =>
                  handleFileChange("COMPROVANTE_ENDERECO", e.target.files[0])
                }
                required
                fileName={
                  files.COMPROVANTE_ENDERECO
                    ? files.COMPROVANTE_ENDERECO.name
                    : ""
                }
              />
            </section>
          </div>

          <div className="inscricao-column">
            <section className="surface-section inscricao-section inscricao-section-optional">
              <h2>Documentos Opcionais</h2>
              <MultiFileInput
                label="Comprovante de Tempo de Serviço"
                onAddMany={(incomingFiles) =>
                  handleMultiFileAddMany("TEMPO_SERVICO", incomingFiles)
                }
                files={files.TEMPO_SERVICO}
                onRemove={(index) =>
                  handleFileRemove("TEMPO_SERVICO", index)
                }
              />

              <MultiFileInput
                label="Diploma de Graduação"
                onAddMany={(incomingFiles) =>
                  handleMultiFileAddMany("DIPLOMA_GRADUACAO", incomingFiles)
                }
                files={files.DIPLOMA_GRADUACAO}
                onRemove={(index) =>
                  handleFileRemove("DIPLOMA_GRADUACAO", index)
                }
              />

              <MultiFileInput
                label="Certificado de Cursos"
                onAddMany={(incomingFiles) =>
                  handleMultiFileAddMany("CERTIFICADO_CURSO", incomingFiles)
                }
                files={files.CERTIFICADO_CURSO}
                onRemove={(index) =>
                  handleFileRemove("CERTIFICADO_CURSO", index)
                }
              />
            </section>
          </div>
        </div>

        <div className="form-actions inscricao-actions-bar">
          <button type="submit" className="btn-modern btn-file" disabled={isSubmitting}>
            <FaFileUpload size={18} />
            <span>{isSubmitting ? "Enviando inscrição..." : "Finalizar Inscrição"}</span>
          </button>
        </div>
      </form>
    </div>
  );
}

export default InscreverEdital;
